import {ICONS} from './icons/mute';
import {VisibilityChecker} from './VisibilityChecker';

// const HAS_BEEN_MANUALLY_PAUSED_ATTR = "has_been_manually_paused";

export class VideoVisibilityCheckerHandler {
	private static _checker: VideoVisibilityChecker;

	static init() {
		document.addEventListener('DOMContentLoaded', () => {
			this._checker = this._checker || new VideoVisibilityChecker();
		});
	}
	static reset() {
		if (this._checker) {
			this._checker.reset();
		}
	}
}

function _fadeOut(image: HTMLImageElement) {
	return;
	const opacityStr = image.style.opacity || '1';
	const opacity = parseFloat(opacityStr);
	if (opacity <= 0) {
		return;
	}
	image.style.opacity = `${opacity - 0.05}`;
	setTimeout(() => {
		_fadeOut(image);
	}, 50);
}

// https://mdn.github.io/dom-examples/fullscreen-api/index.html
function toggleFullScreen(video: HTMLVideoElement) {
	if (!document.fullscreenElement) {
		// If the document is not in full screen mode
		// make the video full screen
		video.requestFullscreen();
	} else {
		// Otherwise exit the full screen
		if (document.exitFullscreen) {
			document.exitFullscreen();
		}
	}
}

// checks video visibility
class VideoVisibilityChecker {
	private _boundListener: () => void = this._onScroll.bind(this);
	private _videoControllers: VideoController[] = [];

	constructor() {
		this.init();

		this._setOnScrollEvents();
		this._setOnPauseEvents();
		setTimeout(() => {
			this._checkVideosVisibility();
		}, 500);
	}

	// TODO: check when calling that with turbolinks if used
	destroy() {
		this.remove_events();
	}
	init() {
		this._videoControllers = this._findVideos();
	}
	reset() {
		this.init();
	}

	private _setOnScrollEvents() {
		window.addEventListener('scroll', this._boundListener, {passive: true});
	}
	private _setOnPauseEvents() {
		for (let i = 0; i < this._videoControllers.length; i++) {
			this._videoControllers[i].setOnPauseEvent();
		}
	}
	private remove_events() {
		window.removeEventListener('scroll', this._boundListener);
	}

	private _findVideos() {
		const elements = document.getElementsByClassName('scene_video') as HTMLCollectionOf<HTMLElement>;
		const list: HTMLElement[] = [];
		for (let i = 0; i < elements.length; i++) {
			list.push(elements[i]);
		}
		const elements_no_top = document.getElementsByClassName('scene_video_no_top') as HTMLCollectionOf<HTMLElement>;
		for (let i = 0; i < elements_no_top.length; i++) {
			list.push(elements_no_top[i]);
		}

		return list.map((e) => new VideoController(e));
	}
	private _onScroll() {
		this._checkVideosVisibility();
	}
	private _checkVideosVisibility() {
		for (let videoController of this._videoControllers) {
			videoController.checkVideosVisibility();
		}
	}
}

class VideoController {
	private _videoElement: HTMLVideoElement | null = null;
	private _playButtonElement: HTMLElement | null = null;
	private _soundButton: HTMLElement | null = null;
	constructor(public readonly element: HTMLElement) {}
	setOnPauseEvent() {
		const on_pause_event = () => {
			if (VisibilityChecker.isElementCenterInViewport(this.element)) {
				this.toggleToVideoAndPlay();
			}
		};
		this.element.addEventListener('pause', on_pause_event, false);
	}
	checkVideosVisibility() {
		const isVisible = VisibilityChecker.isElementCenterInViewport(this.element);
		if (isVisible) {
			this.toggleToVideoAndPlay();
		} else {
			this._pauseVideo();
		}
	}

	private _createVideoElement() {
		const element = this.element;
		const image = element.querySelector('img') as HTMLImageElement;
		const src = element.dataset['src'];
		if (src) {
			const videoClassesStr = element.dataset['videoClass'];
			const withSound = element.dataset['videoWithsound'] != null;

			const video = document.createElement('video');
			const imageClasses = image.classList.toString().split(' ');
			for (let imageClass of imageClasses) {
				if (imageClass.length > 0) {
					video.classList.add(imageClass);
				}
			}
			if (videoClassesStr) {
				const videoClasses = videoClassesStr.split(' ');
				for (let videoClass of videoClasses) {
					video.classList.add(videoClass);
				}
			}

			video.poster = image.src;
			video.loop = true;
			video.autoplay = true;
			video.muted = true;
			video.controls = false;
			// video.setAttribute("controls", "false");
			const source = document.createElement('source');
			source.setAttribute('src', src);
			source.setAttribute('type', 'video/mp4');
			video.appendChild(source);
			element.insertBefore(video, image.nextElementSibling);

			// add fullscreen button
			const fullscreenButton = document.createElement('div');
			fullscreenButton.innerHTML = ICONS.FULLSCREEN;
			element.appendChild(fullscreenButton);
			fullscreenButton.style.position = 'absolute';
			fullscreenButton.style.bottom = '0px';
			fullscreenButton.style.right = '0px';
			fullscreenButton.style.cursor = 'pointer';
			fullscreenButton.style.backgroundColor = 'black';
			fullscreenButton.style.color = 'white';
			// fullscreenButton.style.width = '20px';
			// fullscreenButton.style.height = '20px';
			fullscreenButton.addEventListener('click', () => {
				toggleFullScreen(video);
			});

			// add sound button
			if (withSound) {
				this._soundButton = document.createElement('div');
				this._soundButton.innerHTML = ICONS.VOLUME_UP;
				element.appendChild(this._soundButton);
				this._soundButton.style.position = 'absolute';
				this._soundButton.style.top = '0px';
				this._soundButton.style.right = '0px';
				this._soundButton.style.cursor = 'pointer';
				this._soundButton.style.backgroundColor = 'black';
				this._soundButton.style.color = 'white';
				this._soundButton.addEventListener('click', () => {
					this._toggleSound();
				});
			}

			this._playButtonElement = element.querySelector('.play-button') as HTMLElement;
			this._playButtonElement.addEventListener('click', () => {
				this.toggleToVideoAndPlay();
			});

			// fade out image
			_fadeOut(image);
			return video;
		}
		return null;
	}

	private _toggleSound() {
		const video = this.videoElement();
		if (video && this._soundButton) {
			video.muted = !video.muted;
			const icon = video.muted ? ICONS.VOLUME_UP : ICONS.MUTE;
			this._soundButton.innerHTML = icon;
		}
	}

	videoElement() {
		return (this._videoElement = this._videoElement || this._createVideoElement());
	}

	toggleToVideoAndPlay() {
		this._playVideo();
	}
	private _playVideo() {
		// wrap in a try, to avoid browser errors such as
		// Uncaught (in promise) DOMException: play() failed because the user didn't interact with the document first
		try {
			// use a promise
			// https://developers.google.com/web/updates/2017/09/autoplay-policy-changes
			const promise = this.videoElement()?.play();
			if (promise !== undefined) {
				promise
					.then((_) => {
						this._playButtonElement?.remove();
						// Autoplay started!
					})
					.catch((error) => {
						// Autoplay was prevented.
						this._playButtonElement?.classList.remove('hidden');
					});
			}
		} catch (e) {}
	}

	private _pauseVideo() {
		const element = this.element;

		const video = element.children[1] as HTMLVideoElement;
		if (video) {
			// wrap in a try, to avoid browser errors such as
			// Uncaught (in promise) DOMException: play() failed because the user didn't interact with the document first
			try {
				video.pause();
			} catch (e) {}
		}
	}
}
